/** Knowledge in plain words (ADR-0064): collections, uploads (PDF/Word), refresh, passages. */
export default {
  en: {
    cancel: "Cancel",
    technical: "Technical details",
    lang: { ar: "Arabic", en: "English" },
    classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    collectionMeta: "Who can find these: people cleared for {{classification}} data",
    collectionOrg: "in {{org}}",
    collectionUsedBy: "used by {{agents}}",
    collectionUnused: "no released agent uses it yet",
    source: "Source",
    uploaded: "Uploaded file",
    passages: "Searchable parts",
    showDeleted: "Show removed documents ({{count}})",
    docsEmpty: "No documents yet. Add a PDF, a Word file or some text.",
    empty: {
      title: "No knowledge yet",
      curator: "Create a collection, then add your documents. Agents use it to answer with sources.",
      reader: "No collection yet. A knowledge curator (Administration › Users & roles) creates collections and adds documents.",
      cta: "Create your first collection",
    },
    collection: {
      title: "New knowledge collection",
      name: "Name",
      namePlaceholder: "For example: HR policies",
      nameHelp: "A short name people will recognise.",
      savedAs: "Saved as {{name}}",
      description: "What is in it",
      classification: "Most sensitive content allowed",
      classificationHelp: "Documents above this level cannot be added.",
      model: "Search model",
      sizeKnown: "Vector size {{size}}, taken from the model.",
      sizeLearned: "The vector size is learned from the model when you create the collection.",
      advanced: "Advanced (platform administrators)",
      dimension: "Vector size override",
      dimensionHelp: "Only when the model's size is unknown or wrong. A wrong value breaks adding documents.",
      after: "Next you add documents: PDF, Word or text files.",
      create: "Create",
      created: "Collection {{name}} created",
    },
    doc: {
      title: "Add a document to {{collection}}",
      file: "Document",
      fileHelp: "PDF, Word (.docx), text (.txt), Markdown (.md) or a table (.csv, .tsv), up to 25 MB. Scanned pages are read with OCR.",
      tableNote: "{{count}} rows read. Each row is kept as one line of searchable text, e.g. “Supplier: Acme; Amount: 120”.",
      tableAgents: "Search finds rows by their words. To compare, match or total the figures, give the file to an agent with a “Read a spreadsheet” step (and “Match two lists” or “Group and total”).",
      tableCut: "Only the first 5,000 rows are kept. For larger tables, read the file in an agent with a “Read a spreadsheet” step, or run the agent over it with “Run on many items”.",
      choose: "Choose a file",
      reading: "Reading the document…",
      read_one: "{{name}}: {{count}} page read",
      read_other: "{{name}}: {{count}} pages read",
      readError: {
        unsupported: "This type of file is not accepted. Use PDF, Word (.docx), .txt, .md, .csv or .tsv.",
        too_large: "The file is larger than 25 MB. Split it or remove images.",
        encrypted: "The file is password-protected. Save a copy without a password.",
        unsafe: "The file contains macros or active content and was refused. Save it as a plain PDF or .docx.",
        unreadable: "No text could be read from this file. If it is a scan, check that it is clear.",
        failed: "The document could not be read. Try again, or paste the text below.",
      },
      fieldTitle: "Title",
      text: "Text",
      textHelp: "Filled in from the file. You can also paste or correct the text.",
      who: "Who may find it",
      everyone: "Everyone in the organisation",
      someGroups: "Only some groups",
      groups: "Groups",
      groupsHelp: "Only members of these groups get answers from this document.",
      noGroups: "No groups found",
      language: "Language",
      classification: "Sensitivity",
      version: "Version",
      save: "Add",
      result: {
        created_one: "Document added: {{count}} passage",
        created_other: "Document added: {{count}} passages",
        updated_one: "New version added: {{count}} passage",
        updated_other: "New version added: {{count}} passages",
        unchanged_one: "Nothing changed: this version is already there",
        unchanged_other: "Nothing changed: this version is already there",
      },
    },
    refresh: {
      button: "Refresh from source",
      help: "Re-reads documents that come from a connected folder or website and adds new versions.",
      done: "Checked {{checked}} documents",
      changed_one: "{{count}} updated",
      changed_other: "{{count}} updated",
      unchanged_one: "{{count}} already up to date",
      unchanged_other: "{{count}} already up to date",
      missing_one: "{{count}} no longer exists at the source and was removed",
      missing_other: "{{count}} no longer exist at the source and were removed",
      uploaded_one: "{{count}} was uploaded by hand: upload a new copy to update it",
      uploaded_other: "{{count}} were uploaded by hand: upload a new copy to update them",
      failedSome_one: "{{count}} could not be refreshed: try again later",
      failedSome_other: "{{count}} could not be refreshed: try again later",
      failed: "The refresh failed",
    },
    issues: {
      name_invalid: "Use a name with some Latin letters or digits.",
      embedding_required: "Choose a search model.",
      dimension_invalid: "The vector size must be between 2 and 4096.",
      title_required: "Enter a title.",
      text_required: "Choose a file or paste the text.",
      groups_required: "Choose at least one group, or let everyone find it.",
    },
  },
  ar: {
    cancel: "إلغاء",
    technical: "تفاصيل تقنية",
    lang: { ar: "العربية", en: "الإنجليزية" },
    classification: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيّد" },
    collectionMeta: "من يجد هذه المستندات: من لديهم صلاحية بيانات {{classification}}",
    collectionOrg: "في {{org}}",
    collectionUsedBy: "يستخدمها {{agents}}",
    collectionUnused: "لا يستخدمها أي وكيل مُطلق بعد",
    source: "المصدر",
    uploaded: "ملف مرفوع",
    passages: "الأجزاء القابلة للبحث",
    showDeleted: "إظهار المستندات المحذوفة ({{count}})",
    docsEmpty: "لا توجد مستندات بعد. أضف ملف PDF أو Word أو نصاً.",
    empty: {
      title: "لا توجد معرفة بعد",
      curator: "أنشئ مجموعة ثم أضف مستنداتك. يستخدمها الوكلاء للإجابة مع ذكر المصادر.",
      reader: "لا توجد مجموعات بعد. ينشئ أمناء المعرفة (الإدارة › المستخدمون والأدوار) المجموعات ويضيفون المستندات.",
      cta: "أنشئ مجموعتك الأولى",
    },
    collection: {
      title: "مجموعة معرفة جديدة",
      name: "الاسم",
      namePlaceholder: "مثلاً: HR policies",
      nameHelp: "اسم قصير يعرفه الجميع.",
      savedAs: "يُحفظ باسم {{name}}",
      description: "ماذا تحتوي",
      classification: "أعلى مستوى حساسية مسموح",
      classificationHelp: "لا يمكن إضافة مستندات أعلى من هذا المستوى.",
      model: "نموذج البحث",
      sizeKnown: "حجم المتجه {{size}}، مأخوذ من النموذج.",
      sizeLearned: "يُعرف حجم المتجه من النموذج عند إنشاء المجموعة.",
      advanced: "متقدم (لمسؤولي المنصة)",
      dimension: "تجاوز حجم المتجه",
      dimensionHelp: "فقط عندما يكون حجم النموذج غير معروف أو خاطئاً. القيمة الخاطئة تمنع إضافة المستندات.",
      after: "بعدها تضيف المستندات: ملفات PDF أو Word أو نصوص.",
      create: "إنشاء",
      created: "تم إنشاء المجموعة {{name}}",
    },
    doc: {
      title: "إضافة مستند إلى {{collection}}",
      file: "المستند",
      fileHelp: "PDF أو Word (.docx) أو نص (.txt) أو Markdown (.md) أو جدول (.csv أو .tsv)، حتى 25 ميغابايت. تُقرأ الصفحات الممسوحة ضوئياً بتقنية التعرف على النص.",
      tableNote: "قُرئ {{count}} صفاً. يُحفظ كل صف سطراً واحداً من نص قابل للبحث، مثل «المورّد: أكمي؛ المبلغ: 120».",
      tableAgents: "يجد البحث الصفوف بكلماتها. لمقارنة الأرقام أو مطابقتها أو جمعها، أعطِ الملف لوكيل فيه خطوة «قراءة جدول بيانات» (و«مطابقة قائمتين» أو «التجميع والإجمالي»).",
      tableCut: "تُحفظ أول 5,000 صف فقط. للجداول الأكبر، اقرأ الملف في وكيل بخطوة «قراءة جدول بيانات»، أو شغّل الوكيل عليه بـ«التشغيل على عناصر كثيرة».",
      choose: "اختر ملفاً",
      reading: "جارٍ قراءة المستند…",
      read_zero: "{{name}}: لم تُقرأ أي صفحة",
      read_one: "{{name}}: قُرئت صفحة واحدة",
      read_two: "{{name}}: قُرئت صفحتان",
      read_few: "{{name}}: قُرئت {{count}} صفحات",
      read_many: "{{name}}: قُرئت {{count}} صفحة",
      read_other: "{{name}}: قُرئت {{count}} صفحة",
      readError: {
        unsupported: "هذا النوع من الملفات غير مقبول. استخدم PDF أو Word (.docx) أو .txt أو .md أو .csv أو .tsv.",
        too_large: "الملف أكبر من 25 ميغابايت. قسّمه أو احذف الصور.",
        encrypted: "الملف محمي بكلمة مرور. احفظ نسخة بلا كلمة مرور.",
        unsafe: "يحتوي الملف على وحدات ماكرو أو محتوى نشط فرُفض. احفظه كملف PDF أو .docx عادي.",
        unreadable: "تعذرت قراءة أي نص من الملف. إن كان ممسوحاً ضوئياً فتأكد من وضوحه.",
        failed: "تعذرت قراءة المستند. حاول مجدداً أو الصق النص أدناه.",
      },
      fieldTitle: "العنوان",
      text: "النص",
      textHelp: "يُملأ من الملف. يمكنك أيضاً لصق النص أو تصحيحه.",
      who: "من يمكنه العثور عليه",
      everyone: "كل من في المؤسسة",
      someGroups: "مجموعات محددة فقط",
      groups: "المجموعات",
      groupsHelp: "يحصل أعضاء هذه المجموعات فقط على إجابات من هذا المستند.",
      noGroups: "لم يُعثر على مجموعات",
      language: "اللغة",
      classification: "الحساسية",
      version: "الإصدار",
      save: "إضافة",
      result: {
        created_zero: "أُضيف المستند",
        created_one: "أُضيف المستند: مقطع واحد",
        created_two: "أُضيف المستند: مقطعان",
        created_few: "أُضيف المستند: {{count}} مقاطع",
        created_many: "أُضيف المستند: {{count}} مقطعاً",
        created_other: "أُضيف المستند: {{count}} مقطع",
        updated_zero: "أُضيف إصدار جديد",
        updated_one: "أُضيف إصدار جديد: مقطع واحد",
        updated_two: "أُضيف إصدار جديد: مقطعان",
        updated_few: "أُضيف إصدار جديد: {{count}} مقاطع",
        updated_many: "أُضيف إصدار جديد: {{count}} مقطعاً",
        updated_other: "أُضيف إصدار جديد: {{count}} مقطع",
        unchanged_zero: "لم يتغير شيء: هذا الإصدار موجود",
        unchanged_one: "لم يتغير شيء: هذا الإصدار موجود",
        unchanged_two: "لم يتغير شيء: هذا الإصدار موجود",
        unchanged_few: "لم يتغير شيء: هذا الإصدار موجود",
        unchanged_many: "لم يتغير شيء: هذا الإصدار موجود",
        unchanged_other: "لم يتغير شيء: هذا الإصدار موجود",
      },
    },
    refresh: {
      button: "تحديث من المصدر",
      help: "يعيد قراءة المستندات القادمة من مجلد أو موقع متصل ويضيف الإصدارات الجديدة.",
      done: "تم فحص {{checked}} مستند",
      changed_zero: "لم يُحدَّث أي مستند",
      changed_one: "حُدّث مستند واحد",
      changed_two: "حُدّث مستندان",
      changed_few: "حُدّثت {{count}} مستندات",
      changed_many: "حُدّث {{count}} مستنداً",
      changed_other: "حُدّث {{count}} مستند",
      unchanged_zero: "لا توجد مستندات محدّثة مسبقاً",
      unchanged_one: "مستند واحد محدّث مسبقاً",
      unchanged_two: "مستندان محدّثان مسبقاً",
      unchanged_few: "{{count}} مستندات محدّثة مسبقاً",
      unchanged_many: "{{count}} مستنداً محدّثاً مسبقاً",
      unchanged_other: "{{count}} مستند محدّث مسبقاً",
      missing_zero: "لم يُحذف أي مستند",
      missing_one: "مستند واحد لم يعد موجوداً في المصدر فحُذف",
      missing_two: "مستندان لم يعودا موجودين في المصدر فحُذفا",
      missing_few: "{{count}} مستندات لم تعد موجودة في المصدر فحُذفت",
      missing_many: "{{count}} مستنداً لم يعد موجوداً في المصدر فحُذف",
      missing_other: "{{count}} مستند لم يعد موجوداً في المصدر فحُذف",
      uploaded_zero: "لا توجد مستندات مرفوعة يدوياً",
      uploaded_one: "مستند واحد رُفع يدوياً: ارفع نسخة جديدة لتحديثه",
      uploaded_two: "مستندان رُفعا يدوياً: ارفع نسخاً جديدة لتحديثهما",
      uploaded_few: "{{count}} مستندات رُفعت يدوياً: ارفع نسخاً جديدة لتحديثها",
      uploaded_many: "{{count}} مستنداً رُفع يدوياً: ارفع نسخاً جديدة لتحديثها",
      uploaded_other: "{{count}} مستند رُفع يدوياً: ارفع نسخاً جديدة لتحديثها",
      failedSome_zero: "لا توجد إخفاقات",
      failedSome_one: "تعذر تحديث مستند واحد: حاول لاحقاً",
      failedSome_two: "تعذر تحديث مستندين: حاول لاحقاً",
      failedSome_few: "تعذر تحديث {{count}} مستندات: حاول لاحقاً",
      failedSome_many: "تعذر تحديث {{count}} مستنداً: حاول لاحقاً",
      failedSome_other: "تعذر تحديث {{count}} مستند: حاول لاحقاً",
      failed: "فشل التحديث",
    },
    issues: {
      name_invalid: "استخدم اسماً يحتوي على أحرف لاتينية أو أرقام.",
      embedding_required: "اختر نموذج البحث.",
      dimension_invalid: "يجب أن يكون حجم المتجه بين 2 و4096.",
      title_required: "أدخل عنواناً.",
      text_required: "اختر ملفاً أو الصق النص.",
      groups_required: "اختر مجموعة واحدة على الأقل، أو اسمح للجميع بالعثور عليه.",
    },
  },
};
